import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  useKpiActionItemMutations,
  useKpiCommentMutations,
  useKpiComments,
  useKpiReviewMutations,
  useKpiReviews,
  useProjectEvents,
  useUpdateConnection,
} from "../hooks";
import type { KpiActionItem, KpiReview, PowerProject } from "../types";
import { fmtDate, fmtDateTime } from "../utils";
import { DeadlineBadge, EmptyState, KpiActionBadge, KpiReviewBadge, SectionCard, isKpiUnfinished } from "./ui";
import { ProjectEventList } from "./ProgressTimelineTab";
import { KpiBacklogTree, currentMonday } from "./KpiBacklogTree";
import { CommentBody } from "./CommentBody";

// ── attendees picker (chips; empty = nobody — unlike the Friday "Wszyscy" idiom) ──
function AttendeesPicker({ selected, onToggle }: { selected: string[]; onToggle: (id: string) => void }) {
  const { data: rawUsers = [] } = useInternalUsers();
  const users = useMemo(
    () => [...rawUsers].sort((a, b) => a.full_name.localeCompare(b.full_name, "pl")),
    [rawUsers],
  );
  return (
    <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
      {users.map((u) => {
        const active = selected.includes(u.id);
        return (
          <button
            key={u.id}
            type="button"
            onClick={() => onToggle(u.id)}
            title={u.full_name}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
              active
                ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 hover:border-[var(--color-primary)]"
            }`}
          >
            <UserAvatar userId={u.id} fullName={u.full_name} profilePictureUrl={u.profile_picture_url} size="xs" />
            {u.full_name}
          </button>
        );
      })}
    </div>
  );
}

function FacilitatorSelect({ value, onChange }: { value: string | null; onChange: (val: string | null) => void }) {
  const { data: users = [] } = useInternalUsers();
  const options = useMemo(
    () => [...users].sort((a, b) => a.full_name.localeCompare(b.full_name, "pl"))
      .map((u) => ({ value: u.id, label: u.full_name })),
    [users],
  );
  return (
    <SearchableSelect
      options={options}
      value={value ?? ""}
      placeholder="— wybierz —"
      onChange={(e) => onChange(e.target.value || null)}
    />
  );
}

// "zrobione 2.06.2026 · Jan Kowalski · po KPI #3" — shows whatever completion data exists.
function doneMeta(item: KpiActionItem): string {
  const parts = [
    item.done_at ? fmtDate(item.done_at) : null,
    item.done_by_name,
    item.done_review_sequence_no != null ? `po KPI #${item.done_review_sequence_no}` : null,
  ].filter(Boolean);
  return parts.length ? `zrobione: ${parts.join(" · ")}` : "zrobione";
}

function SnapshotGrid({ review }: { review: KpiReview }) {
  const s = review.progress_snapshot ?? {};
  if (s.parcels_total == null && s.milestones_total == null) return null;
  return (
    <div className="grid grid-cols-3 gap-2 text-center">
      <div className="card px-2 py-1.5">
        <p className="section-label">Działki</p>
        <p className="font-mono text-sm font-bold">{s.parcels_ok ?? 0}/{s.parcels_total ?? 0}</p>
      </div>
      <div className="card px-2 py-1.5">
        <p className="section-label">Kamienie</p>
        <p className="font-mono text-sm font-bold">{s.milestones_done ?? 0}/{s.milestones_total ?? 0}</p>
      </div>
      <div className="card px-2 py-1.5">
        <p className="section-label">Uzgodnienia</p>
        <p className="font-mono text-sm font-bold">{s.open_agreements ?? 0} otwarte</p>
      </div>
    </div>
  );
}

// ── current period (open/planned KPI) — hosts the rolling backlog ─────────────────
function CurrentPeriodCard({
  review,
  connection,
  openItems,
  prevHeldAt,
  onClosed,
}: {
  review: KpiReview;
  connection: PowerProject;
  openItems: KpiActionItem[];
  prevHeldAt: string | null;
  onClosed: () => void;
}) {
  const m = useKpiReviewMutations(connection.id);
  const [agenda, setAgenda] = useState(review.agenda);
  const [summary, setSummary] = useState(review.summary);
  const [collapsed, setCollapsed] = useState(true); // current-period plan starts folded

  const { data: allEvents = [] } = useProjectEvents(connection.id);
  const sinceEvents = useMemo(
    () => (prevHeldAt ? allEvents.filter((e) => e.occurred_at >= prevHeldAt) : allEvents),
    [allEvents, prevHeldAt],
  );

  const toggleAttendee = (id: string) =>
    m.update.mutate({
      id: review.id,
      payload: {
        attendees: review.attendees.includes(id)
          ? review.attendees.filter((x) => x !== id)
          : [...review.attendees, id],
      },
    });

  return (
    <SectionCard
      className="max-w-3xl"
      title={`Bieżący okres — KPI #${review.sequence_no} (w przygotowaniu)`}
      action={
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            title={collapsed ? "Rozwiń plan" : "Zwiń plan"}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </button>
          <KpiReviewBadge status={review.status} />
        </div>
      }
    >
      {collapsed ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          {openItems.length} nieskończonych ustaleń · termin spotkania {fmtDate(review.scheduled_for)}
          <span className="block font-mono text-[11px] text-[var(--color-text-muted)]">
            Rozwiń, żeby dopisać ustalenia ze spotkania.
          </span>
        </p>
      ) : (
      <div className="space-y-4">
        <AddAgreement reviewId={review.id} connectionId={connection.id} />

        <div className="grid sm:grid-cols-2 gap-3 border-t border-[var(--color-border-muted)] pt-3">
          <label className="block">
            <span className="section-label">Termin spotkania</span>
            <DatePicker
              className="mt-1"
              value={review.scheduled_for}
              onChange={(v) => m.update.mutate({ id: review.id, payload: { scheduled_for: v } })}
            />
          </label>
          <div className="block">
            <span className="section-label">Prowadzący</span>
            <div className="mt-1">
              <FacilitatorSelect
                value={review.facilitator}
                onChange={(val) => m.update.mutate({ id: review.id, payload: { facilitator: val } })}
              />
            </div>
          </div>
        </div>

        <div>
          <span className="section-label">Uczestnicy</span>
          <div className="mt-1">
            <AttendeesPicker selected={review.attendees} onToggle={toggleAttendee} />
          </div>
        </div>

        <div>
          <span className="section-label">Agenda / do omówienia</span>
          <textarea className="input-base mt-1 min-h-[56px]" value={agenda} onChange={(e) => setAgenda(e.target.value)} />
          {agenda !== review.agenda && (
            <Button size="sm" variant="ghost" className="mt-1" onClick={() => m.update.mutate({ id: review.id, payload: { agenda } })}>
              Zapisz agendę
            </Button>
          )}
        </div>

        <details className="border-t border-[var(--color-border-muted)] pt-3">
          <summary className="section-label cursor-pointer">
            Co się wydarzyło od ostatniego KPI ({sinceEvents.length})
          </summary>
          <div className="mt-3">
            <ProjectEventList events={sinceEvents} />
          </div>
        </details>

        <div className="border-t border-[var(--color-border-muted)] pt-3">
          <span className="section-label">Podsumowanie / ustalenia</span>
          <textarea
            className="input-base mt-1 min-h-[56px]"
            placeholder="Ustalenia ze spotkania (zapisywane przy zamknięciu)…"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => m.close.mutate({ id: review.id, summary }, { onSuccess: onClosed })}
              disabled={m.close.isPending}
            >
              Zamknij KPI (odbyte)
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { if (confirm("Odwołać to spotkanie KPI?")) m.cancel.mutate(review.id); }}
            >
              Odwołaj
            </Button>
            <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
              Zamknięcie zamraża snapshot postępu i otwiera kolejne KPI. Zadania zostają w backlogu.
            </span>
          </div>
        </div>
      </div>
      )}
    </SectionCard>
  );
}

// ── discussion thread under a KPI (notes / comments, à la tasks) ──────────────────
function KpiCommentThread({ reviewId, connectionId }: { reviewId: string; connectionId: string }) {
  const { data: comments = [], isLoading } = useKpiComments(reviewId);
  const m = useKpiCommentMutations(reviewId, connectionId);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: rawUsers = [] } = useInternalUsers();
  const mentionSuggestions = useMemo(
    () => rawUsers.map((u) => ({ id: u.id, name: u.full_name })),
    [rawUsers],
  );
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftMentions, setDraftMentions] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [editMentions, setEditMentions] = useState<string[]>([]);

  const add = () => {
    if (!draft.trim()) return;
    m.create.mutate(
      { content: draft, mentionIds: draftMentions },
      { onSuccess: () => { setDraft(""); setDraftMentions([]); } },
    );
  };

  return (
    <div className="border-t border-[var(--color-border-muted)] pt-3">
      <button
        type="button"
        className="section-label inline-flex items-center gap-1 hover:text-[var(--color-text-primary)]"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Dyskusja / notatki ({comments.length})
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          {isLoading ? (
            <div className="py-2 flex justify-center"><Spinner /></div>
          ) : comments.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">Brak komentarzy.</p>
          ) : (
            <ul className="space-y-3">
              {comments.map((c) => {
                return (
                  <li key={c.id} className="flex gap-2">
                    <UserAvatar userId={c.author_id ?? ""} fullName={c.author_name} profilePictureUrl={c.author_profile_picture_url} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--color-text-primary)]">{c.author_name}</span>
                        <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                          {fmtDateTime(c.created_at)}{c.is_edited ? " · edytowano" : ""}
                        </span>
                        {c.author_id === currentUserId && editingId !== c.id && (
                          <span className="ml-auto flex items-center gap-2">
                            <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]" title="Edytuj" onClick={() => { setEditingId(c.id); setEditDraft(c.content); setEditMentions(c.mentions.map((x) => x.id)); }}>
                              <Pencil size={12} />
                            </button>
                            <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" title="Usuń" onClick={() => m.remove.mutate(c.id)}>
                              <Trash2 size={12} />
                            </button>
                          </span>
                        )}
                      </div>
                      {editingId === c.id ? (
                        <div className="mt-1">
                          <RichTextEditor
                            value={editDraft}
                            onChange={setEditDraft}
                            onMentionIdsChange={setEditMentions}
                            mentionSuggestions={mentionSuggestions}
                          />
                          <div className="mt-1 flex gap-2">
                            <Button size="sm" disabled={!editDraft.trim()} onClick={() => m.update.mutate({ id: c.id, content: editDraft, mentionIds: editMentions }, { onSuccess: () => setEditingId(null) })}>
                              Zapisz
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Anuluj</Button>
                          </div>
                        </div>
                      ) : (
                        <CommentBody content={c.content} mentions={c.mentions} />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="space-y-2">
            <RichTextEditor
              value={draft}
              onChange={setDraft}
              onMentionIdsChange={setDraftMentions}
              mentionSuggestions={mentionSuggestions}
              placeholder="Napisz komentarz / notatkę…"
            />
            <div className="flex justify-end">
              <Button size="sm" disabled={!draft.trim()} onClick={add}>Dodaj</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── a past (held / cancelled) review (read-only record + discussion) ──────────────
function PastReviewCard({ review, connectionId }: { review: KpiReview; connectionId: string }) {
  const cancelled = review.status === "cancelled";
  return (
    <SectionCard
      className="max-w-3xl"
      title={`KPI #${review.sequence_no} · ${review.held_at ? fmtDateTime(review.held_at) : fmtDate(review.scheduled_for)}`}
      action={<KpiReviewBadge status={review.status} />}
    >
      <div className="space-y-3">
        {cancelled ? (
          <p className="text-sm text-[var(--color-text-muted)]">Spotkanie odwołane.</p>
        ) : (
          <div className="space-y-3">
          <SnapshotGrid review={review} />
          {(review.progress_snapshot?.attendees?.length || review.progress_snapshot?.facilitator) && (
            <p className="text-[12px] font-mono text-[var(--color-text-secondary)]">
              {review.progress_snapshot?.facilitator && <>Prowadzący: {review.progress_snapshot.facilitator} · </>}
              Uczestnicy: {review.progress_snapshot?.attendees?.join(", ") || "—"}
            </p>
          )}
          {review.agenda && (
            <div>
              <span className="section-label">Agenda</span>
              <p className="mt-0.5 text-sm whitespace-pre-wrap text-[var(--color-text-secondary)]">{review.agenda}</p>
            </div>
          )}
          {review.summary && (
            <div>
              <span className="section-label">Ustalenia</span>
              <p className="mt-0.5 text-sm whitespace-pre-wrap text-[var(--color-text-secondary)]">{review.summary}</p>
            </div>
          )}
          {review.action_items.length > 0 && (
            <div>
              <span className="section-label">Zadania zaplanowane na tym KPI</span>
              <ul className="mt-1 space-y-1">
                {review.action_items.map((it) => (
                  <li key={it.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <KpiActionBadge status={it.status} />
                    <span className={it.status === "done" ? "line-through text-[var(--color-text-muted)]" : ""}>
                      {it.description}
                    </span>
                    {it.assignee_name && (
                      <span className="font-mono text-[11px] text-[var(--color-text-muted)]">· {it.assignee_name}</span>
                    )}
                    {it.status === "done" && (
                      <span className="font-mono text-[11px] text-[var(--color-text-muted)]">· {doneMeta(it)}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          </div>
        )}
        <KpiCommentThread reviewId={review.id} connectionId={connectionId} />
      </div>
    </SectionCard>
  );
}

/**
 * Pole ustaleń na karcie spotkania. TO JEST jedyne miejsce, które tworzy zadanie
 * oznaczone jako „ustalenie z KPI" — o pochodzeniu decyduje miejsce dodania, więc to samo
 * zdanie wpisane w drzewie tygodnia będzie zapiskiem własnym.
 */
function AddAgreement({ reviewId, connectionId }: { reviewId: string; connectionId: string }) {
  const m = useKpiActionItemMutations(connectionId);
  const [description, setDescription] = useState("");
  const add = () => {
    if (!description.trim()) return;
    m.create.mutate(
      { review: reviewId, description: description.trim(), origin: "kpi" },
      { onSuccess: () => setDescription("") },
    );
  };
  return (
    <div>
      <span className="section-label">Ustalenia ze spotkania</span>
      <div className="mt-1 flex items-center gap-2">
        <input
          className="input-base flex-1"
          placeholder="Co ustaliliśmy? Enter zapisuje"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
        />
        <Button size="sm" disabled={!description.trim() || m.create.isPending} onClick={add}>
          Dodaj ustalenie
        </Button>
      </div>
      <p className="mt-1 font-mono text-[11px] text-[var(--color-text-muted)]">
        Trafia do backlogu z plakietką „KPI #{""}" i wchodzi do statystyk. Zadania dopisane
        w planie tygodniowym są zapiskami własnymi i statystyk nie ruszają.
      </p>
    </div>
  );
}

/**
 * Zamiast ręcznego poprawiania terminu w każdym zaległym zadaniu — jedno pytanie.
 * Pokazuje się po zamknięciu KPI i na żądanie z nagłówka backlogu.
 */
function CarryOverPrompt({
  connectionId,
  count,
  onDone,
}: {
  connectionId: string;
  count: number;
  onDone: () => void;
}) {
  const m = useKpiActionItemMutations(connectionId);
  return (
    <div className="card flex flex-wrap items-center gap-3 px-3 py-2 border-l-2 border-l-amber-500">
      <span className="text-sm text-[var(--color-text-secondary)]">
        {count} {count === 1 ? "zadanie ma" : "zadań ma"} termin z minionych tygodni. Przenieść na bieżący tydzień?
      </span>
      <Button
        size="sm"
        disabled={m.carryOver.isPending}
        onClick={() => m.carryOver.mutate(undefined, { onSuccess: onDone })}
      >
        Przenieś
      </Button>
      <Button size="sm" variant="ghost" onClick={onDone}>Zostaw</Button>
    </div>
  );
}

// ── tab root ──────────────────────────────────────────────────────────────────
export function KpiTab({ connection }: { connection: PowerProject }) {
  const { data: reviews, isLoading } = useKpiReviews(connection.id);
  const reviewMut = useKpiReviewMutations(connection.id);
  const updateConn = useUpdateConnection(connection.id);
  const [interval, setIntervalDays] = useState<string>(
    connection.kpi_interval_days != null ? String(connection.kpi_interval_days) : "",
  );

  const planned = useMemo(() => (reviews ?? []).find((r) => r.status === "planned"), [reviews]);
  const past = useMemo(() => (reviews ?? []).filter((r) => r.status !== "planned"), [reviews]);
  const prevHeldAt = useMemo(
    () => (reviews ?? []).find((r) => r.status === "held")?.held_at ?? null,
    [reviews],
  );
  const suggested = planned?.suggested_next_date ?? reviews?.[0]?.suggested_next_date ?? null;

  // Rolling backlog = every action item across the project. The tree groups them by the
  // week they are planned for; "otwarte" now means everything not finished.
  const allItems = useMemo(() => (reviews ?? []).flatMap((r) => r.action_items), [reviews]);
  const openItems = useMemo(() => allItems.filter((i) => isKpiUnfinished(i.status)), [allItems]);
  // Karta spotkania mówi o USTALENIACH — zapiski własne nie są tematem przeglądu.
  const openAgreements = useMemo(() => openItems.filter((i) => i.origin === "kpi"), [openItems]);
  const thisMonday = currentMonday();
  const overdueCount = useMemo(
    () => openItems.filter((i) => i.week_start && i.week_start < thisMonday).length,
    [openItems, thisMonday],
  );
  const [carryOverPrompt, setCarryOverPrompt] = useState(false);

  const saveInterval = () => {
    const n = interval.trim() === "" ? null : Math.max(1, parseInt(interval, 10) || 0) || null;
    updateConn.mutate({ kpi_interval_days: n } as Partial<PowerProject>);
  };

  return (
    <div className="space-y-4">
      <SectionCard
        className="max-w-3xl"
        title="KPI — cykliczne przeglądy projektu"
        action={
          <Button
            size="sm"
            variant="secondary"
            disabled={!!planned || reviewMut.create.isPending}
            title={planned ? "Najpierw zamknij lub odwołaj otwarte KPI" : undefined}
            onClick={() => reviewMut.create.mutate({ connection: connection.id, scheduled_for: suggested ?? undefined })}
          >
            + Nowe KPI
          </Button>
        }
      >
        <div className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="section-label">Interwał [dni]</span>
            <div className="mt-1 flex items-center gap-2">
              <input type="number" min={1} className="input-base w-24" value={interval} onChange={(e) => setIntervalDays(e.target.value)} />
              {String(connection.kpi_interval_days ?? "") !== interval && (
                <Button size="sm" variant="ghost" onClick={saveInterval}>Zapisz</Button>
              )}
            </div>
          </label>
          <div>
            <span className="section-label">{planned ? "Najbliższe KPI" : "Sugerowany termin następnego"}</span>
            <div className="mt-1">
              {planned ? (
                <DeadlineBadge status={planned.scheduled_deadline_status} date={planned.scheduled_for} />
              ) : (
                <span className="font-mono text-sm text-[var(--color-text-secondary)]">{fmtDate(suggested)}</span>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      {isLoading ? (
        <div className="py-8 flex justify-center"><Spinner /></div>
      ) : (
        <>
          {/* Backlog jest teraz głównym widokiem zakładki, a nie sekcją schowaną w karcie
              spotkania — praca toczy się między spotkaniami, w tygodniach. */}
          <SectionCard
            title="Zadania — plan tygodniowy"
            action={
              <div className="flex items-center gap-2">
                {/* Każde przyłącze ma też swój automatyczny task we Friday. Bez tego linku
                    ludzie pytają „to gdzie mam pisać" — tu ustalenia projektu, tam reszta firmy. */}
                {connection.friday_task && (
                  <Link
                    to={`/tasks/board?task=${connection.friday_task}`}
                    className="text-xs text-[var(--color-primary)] hover:underline whitespace-nowrap"
                    title="Otwórz zadanie tego przyłącza na tablicy Friday"
                  >
                    Zadanie we Friday →
                  </Link>
                )}
                {overdueCount > 0 && (
                  <Button size="sm" variant="secondary" onClick={() => setCarryOverPrompt(true)}>
                    Przenieś zaległe ({overdueCount})
                  </Button>
                )}
              </div>
            }
          >
            <KpiBacklogTree
              items={allItems}
              connectionId={connection.id}
              coordinators={connection.project_coordinators}
            />
          </SectionCard>

          {carryOverPrompt && (
            <CarryOverPrompt
              connectionId={connection.id}
              count={overdueCount}
              onDone={() => setCarryOverPrompt(false)}
            />
          )}

          {planned && (
            <CurrentPeriodCard
              key={planned.id}
              review={planned}
              connection={connection}
              openItems={openAgreements}
              prevHeldAt={prevHeldAt}
              onClosed={() => { if (overdueCount > 0) setCarryOverPrompt(true); }}
            />
          )}
          {!planned && past.length === 0 && (
            <EmptyState>Brak przeglądów KPI. Utwórz pierwszy przyciskiem „+ Nowe KPI".</EmptyState>
          )}
          {past.map((r) => <PastReviewCard key={r.id} review={r} connectionId={connection.id} />)}
        </>
      )}
    </div>
  );
}
